import { toJS } from "mobx";

import { getServerNow } from "./server-time";

export function parseDatetimeToLocal(datetime: Date | string) {
  datetime = new Date(datetime);
  const localDatetime = new Date(
    datetime.getTime() - datetime.getTimezoneOffset() * 60000,
  );
  return localDatetime.toISOString().slice(0, 19).replace("T", " ");
}

export function dateTimeISOString(datetimeStr?: string) {
  const datetime = datetimeStr == null ? getServerNow() : new Date(datetimeStr);
  return datetime.toISOString();
}

export function dateTimeStrToDateStr(datetimeStr: string) {
  return dateToISOString(new Date(datetimeStr));
}

export function dateToISOString(date: Date): string {
  return date.toISOString().split("T")[0];
}

export function getAge(dateOfBirth: string): number {
  const birth = new Date(dateOfBirth);
  const today = getServerNow();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

export function dateTimeOrNull(
  dateString: string | null | undefined,
  isDateTime: boolean = true,
  locale: string = "sk-SK",
) {
  if (dateString === null || dateString === undefined || dateString === "") {
    return null;
  }
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    return Intl.DateTimeFormat(
      locale,
      isDateTime
        ? {
            dateStyle: "short",
            timeStyle: "medium",
          }
        : {},
    ).format(date);
  } else {
    return null;
  }
}

export function floatRounding(
  value: number | string | null | undefined,
  decimals: number = 1,
) {
  if (typeof value === "number") {
    return value.toFixed(decimals);
  }
  return value;
}

export function getObjectFromArray<T extends string | number>(
  options: T[],
): Record<string, T> {
  return options.reduce<Record<string, T>>((acc, item) => {
    acc[String(item)] = item;
    return acc;
  }, {});
}

/** Useful for inspecting observable objects (otherwise we just see Proxy on the console). */
export function toRawDeep<T>(obj: T): T {
  return toJS(obj);
}

export function snapshot(value: object): string {
  return JSON.stringify(toJS(value));
}

/**
 * Returns the value as an array.
 * If the value is null/undefined, it returns null/undefined instead.
 * If the value is already an array, it just returns the array.
 */
export function maybeAsArray<T>(
  val: T,
): T extends null | undefined ? T : T extends unknown[] ? T : T[] {
  return (val == null ? val : Array.isArray(val) ? val : [val]) as any;
}

export function resolveNestedProperty(obj: object, path: string) {
  return path
    .split(".")
    .reduce<unknown>(
      (acc, key) => acc && (acc as Record<string, unknown>)[key],
      obj,
    );
}

export function uniques<T>(list: T[]): T[] {
  return [...new Set(list)];
}

export class Deferral {
  promise: Promise<void>;

  constructor() {
    this.promise = new Promise((res, rej) => {
      this.resolve = res;
      this.reject = rej;
    });
  }

  resolve!: () => void;

  reject!: (error?: unknown) => void;
}

/** Transpose 2D array. Works with variable length rows as well. */
export function transpose<T>(matrix: T[][]) {
  const maxRows = Math.max(...matrix.map((a) => a.length));
  const res: T[][] = [];

  for (let i = 0; i < maxRows; i++) {
    res.push(matrix.map((row) => row[i]));
  }

  return res;
}

export function compareString(a: string, b: string) {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

export function compareDateStr(date1: string, date2: string) {
  if (date1 < date2) {
    return -1;
  }
  if (date1 > date2) {
    return 1;
  }
  return 0;
}

export function assert(cond: unknown, msg = "Assertion failed"): asserts cond {
  if (!cond) {
    throw new Error(msg);
  }
}

/**
 * Returns the value, or throws when it's null or undefined.
 *
 * Prefer the classic `assert` for statement-position checks; this one is for
 * expression positions where asserting inline is impractical (e.g. JSX props).
 */
export function requireNonNull<T>(value: T): NonNullable<T> {
  assert(value != null, "Required value is null or undefined");
  return value;
}

export function maybeCapitalizeFirstLetter<T extends string | null>(str: T): T {
  return str != null && str.length > 0
    ? (`${str[0].toLocaleUpperCase()}${str.slice(1)}` as T)
    : str;
}

/**
 * Pin selected items to the top of an array while preserving the relative order of both selected and unselected items.
 * Used for filter options and table rows to visually highlight active selections.
 */
export function pinSelectedToTop<T>(
  items: T[],
  selected: any[],
  idExtractor: (item: T) => any,
): T[] {
  if (selected.length === 0) {
    return items;
  }

  const selectedSet = new Set(selected);
  const selectedItems: T[] = [];
  const rest: T[] = [];

  for (const item of items) {
    if (selectedSet.has(idExtractor(item))) {
      selectedItems.push(item);
    } else {
      rest.push(item);
    }
  }

  return selectedItems.concat(rest);
}

export function createRowId(): string {
  // crypto.randomUUID is only defined in secure contexts (HTTPS / localhost).
  // Fall back to a non-cryptographic id so plain-http QA hosts don't throw —
  // these ids are only used as React keys, not for anything security-sensitive.
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
}

const BYTE_UNITS = ["byte", "kilobyte", "megabyte", "gigabyte"] as const;

/**
 * Formats a byte count as a localized file size ("1.4 MB"). Decimal (1000-based)
 * steps, matching the `Intl` unit names — and what the OS file browsers report.
 */
export function formatBytes(bytes: number, locale: string): string {
  let value = bytes;
  let unit = 0;
  while (value >= 1000 && unit < BYTE_UNITS.length - 1) {
    value /= 1000;
    unit++;
  }
  return Intl.NumberFormat(locale, {
    style: "unit",
    unit: BYTE_UNITS[unit],
    unitDisplay: "short",
    maximumFractionDigits: unit === 0 ? 0 : 1,
  }).format(value);
}
